import Accordion from "@/components/Accordion";
import { LanguageConfigModel, StyleConfigModel } from "@/pages/Messaging/types";
import { Toggle } from "@/components/Toggle";
import React, { useEffect, useState } from "react";
import TextInput from "../../TextInput";
import { ChatWindow } from "../../types";
import useApi from "@/hooks/useApi";
import FeatureHint from "@/components/FeatureHint";

interface Props {
  onExpanded(): void;
  workingChatWindow: ChatWindow;
  setWorkingChatWindow: (chatWindow: ChatWindow) => void;
}

interface ProviderCap {
  providerSlug: string;
  providerName: string;
  available: boolean;
  missingReason?: string;
  setupUrl?: string;
}

export function RequestCall(props: Props) {
  const api = useApi();
  const style = props.workingChatWindow.styleConfig || {};
  const lang  = props.workingChatWindow.languageConfig || {};

  const [caps, setCaps] = useState<ProviderCap[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.messaging.get("WidgetCapabilities/outbound");
        if (cancelled) return;
        if (r.ok) setCaps(await r.json() as ProviderCap[]);
        else setCaps([]);
      } catch { if (!cancelled) setCaps([]); }
    })();
    return () => { cancelled = true; };
  }, [api]);

  const updateStyle = (u: Partial<StyleConfigModel>) =>
    props.setWorkingChatWindow({ ...props.workingChatWindow, styleConfig: { ...style, ...u } });
  const updateLang = (u: Partial<LanguageConfigModel>) =>
    props.setWorkingChatWindow({ ...props.workingChatWindow, languageConfig: { ...lang, ...u } });

  const available = (caps ?? []).filter(c => c.available);
  const activeSlug = style.requestCallProviderSlug ?? available[0]?.providerSlug;

  return (
    <Accordion.Item eventKey="request-call" onExpanded={() => props.onExpanded()}>
      <Accordion.Header>
        <Accordion.Title>Request a Call</Accordion.Title>
        <Accordion.SubTitle>
          One-click outbound call or scheduled callback, routed through your voice provider
        </Accordion.SubTitle>
      </Accordion.Header>
      <Accordion.Body>
        <div className="mb-2">
          <div className="flex justify-between items-center">
            <div className="text-sm font-medium">Enable</div>
            <Toggle
              toggled={!!style.requestCallEnabled}
              changeToggle={() => updateStyle({ requestCallEnabled: !style.requestCallEnabled })}
            />
          </div>

          {style.requestCallEnabled && (
            <>
              <div className="my-3 border-t border-slate-200" />

              <div className="flex justify-between items-center">
                <div className="text-sm font-medium">Allow "Call me now"</div>
                <Toggle
                  toggled={style.requestCallAllowImmediate ?? true}
                  changeToggle={() => updateStyle({ requestCallAllowImmediate: !(style.requestCallAllowImmediate ?? true) })}
                />
              </div>
              <div className="flex justify-between items-center mt-2">
                <div className="text-sm font-medium">Allow scheduling</div>
                <Toggle
                  toggled={style.requestCallAllowSchedule ?? true}
                  changeToggle={() => updateStyle({ requestCallAllowSchedule: !(style.requestCallAllowSchedule ?? true) })}
                />
              </div>

              <div className="my-3 border-t border-slate-200" />
              <div className="text-sm font-medium mb-2">Outbound call provider</div>
              {caps == null ? (
                <div className="text-xs text-slate-500">Checking connected CTI providers…</div>
              ) : available.length === 0 ? (
                <FeatureHint
                  hintKey="request-call-no-provider"
                  severity="info"
                  title="No outbound voice provider connected"
                  body="Connect Twilio, Telnyx, RingCentral, or any of the 9 supported CTI providers to enable 'Call me now'."
                  cta={{ label: "Browse voice integrations →", to: "/Messaging/Channels/Voice" }}
                />
              ) : (
                <select
                  aria-label="Outbound call provider"
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)]"
                  value={activeSlug ?? ""}
                  onChange={e => updateStyle({ requestCallProviderSlug: e.target.value || undefined })}
                >
                  {available.map(c => (
                    <option key={c.providerSlug} value={c.providerSlug}>{c.providerName}</option>
                  ))}
                </select>
              )}

              <div className="my-3 border-t border-slate-200" />
              <div className="text-sm font-medium mb-2">Title</div>
              <TextInput value={lang.requestCallTitle ?? ""} setValue={v => updateLang({ requestCallTitle: v })} showEmojiButton={false} />
              <div className="text-sm font-medium my-2">Subtitle</div>
              <TextInput value={lang.requestCallSubtitle ?? ""} setValue={v => updateLang({ requestCallSubtitle: v })} showEmojiButton={false} />
              <div className="text-sm font-medium my-2">"Call now" button</div>
              <TextInput value={lang.requestCallNowButton ?? ""} setValue={v => updateLang({ requestCallNowButton: v })} showEmojiButton={false} />
              <div className="text-sm font-medium my-2">"Schedule" button</div>
              <TextInput value={lang.requestCallScheduleButton ?? ""} setValue={v => updateLang({ requestCallScheduleButton: v })} showEmojiButton={false} />
              <div className="text-sm font-medium my-2">Success message</div>
              <TextInput value={lang.requestCallSuccessMessage ?? ""} setValue={v => updateLang({ requestCallSuccessMessage: v })} showEmojiButton={false} />
            </>
          )}
        </div>
      </Accordion.Body>
    </Accordion.Item>
  );
}
